import { Component, OnDestroy, OnInit, LOCALE_ID, Inject, NgZone, ChangeDetectorRef } from "@angular/core";
import { Subscription } from "rxjs";
import { latLng, tileLayer, marker, divIcon, Point, latLngBounds, point } from "leaflet";

import { DeviceType } from "app/models/device.model";
import { ContextService } from "app/services/context.service";
import { ProjectDeviceService } from "app/services/api/projectDevice.service";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { DataTypeModelKey } from "app/models/dataType.model";
import { formatDate } from "@angular/common";
import { EventModelSeverity } from "app/models/event.model";
import { Router } from "@angular/router";
import { DeviceDataType } from "app/models/deviceData.model";
import { IMqttMessage } from "ngx-mqtt";
import { DeviceMqttService } from "app/services/api/device.mqtt.service";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { locale as catalan } from "./i18/ca";
import { locale as english } from "./i18/en";
import { locale as spanish } from "./i18/es";

@Component({
    selector: "app-map",
    templateUrl: "./map.component.html",
    styleUrls: ["./map.component.scss"],
    standalone: false,
})
export class MapComponent implements OnInit, OnDestroy {
    devices: ProjectDeviceModel[] = [];
    currentProject: string;
    showDisabledDevices = false;
    allDevices: ProjectDeviceModel[] = [];

    esriMap = tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 18,
        attribution: "Esri ArcGIS",
    });
    osMap = tileLayer("http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 18,
        attribution: "OpenStreetMap",
    });

    mapOptions = {
        layers: [this.esriMap],
    };
    layersControl = {
        baseLayers: {
            "Esri ArcGIS": this.esriMap,
            "Open Street Map": this.osMap,
        },
    };
    layers = [];
    bounds = null;
    boundsOptions = { padding: point(32, 32) };

    subscriptions: Map<string, Subscription> = new Map<string, Subscription>();

    loadingData = false;

    constructor(
        private projectDeviceService: ProjectDeviceService,
        private sessionService: ContextService,
        @Inject(LOCALE_ID) private locale: string,
        private router: Router,
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
        private readonly deviceMqtt: DeviceMqttService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, spanish, catalan);
    }

    ngOnInit() {
        this.currentProject = this.sessionService.currentProject;

        this.refreshData();
    }

    ngOnDestroy(): void {
        for (const subs of this.subscriptions.values()) {
            subs.unsubscribe();
        }
    }

    private subscribeToTopics(device: ProjectDeviceModel) {
        this.subscriptions.get(device.id)?.unsubscribe();

        const subs = this.deviceMqtt
            .deviceTopic(device.id, DeviceDataType.instant, DataTypeModelKey.soundPressureLevel)
            .subscribe((data: IMqttMessage) => {
                const item = JSON.parse(data.payload.toString());
                this.zone.run(() => {
                    if (!device.lastData) {
                        device.lastData = {} as ProjectDeviceModel["lastData"];
                    }
                    if (!device.lastData.soundPressureLevel) {
                        device.lastData.soundPressureLevel = item;
                    } else {
                        device.lastData.soundPressureLevel.value = item.value;
                        device.lastData.soundPressureLevel.timestamp = item.timestamp;
                    }
                    this.updateMarkers();
                });
            });
        this.subscriptions.set(device.id, subs);
    }

    private refreshData() {
        if (this.loadingData) {
            return;
        }
        this.loadingData = true;

        this.projectDeviceService.getAll(this.currentProject).subscribe(
            (devices) => {
                for (const subs of this.subscriptions.values()) {
                    subs.unsubscribe();
                }
                this.subscriptions.clear();

                this.allDevices = devices;
                this.filterDevices();
                devices.forEach((device) => {
                    this.subscribeToTopics(device);
                });
                this.loadingData = false;
            },
            (error) => {
                console.log(error);
                this.loadingData = false;
            },
        );
    }

    filterDevices() {
        this.devices = this.showDisabledDevices ? this.allDevices : this.allDevices.filter((device) => device.active);
        this.updateMarkers(true);
    }

    onShowDisabledDevicesChange() {
        this.filterDevices();
    }

    updateMarkers(fitBounds = false) {
        const nextLayers = [];
        let nextBounds = null;

        for (let i = 0; i < this.devices.length; i++) {
            //Get device info
            const device = Object.assign(new ProjectDeviceModel(), this.devices[i]);

            //Add marker based on device type
            switch (device.baseDeviceData.deviceType) {
                case DeviceType.NoiseSensor:
                case DeviceType.AcousticLimiter: {
                    const value = device.getLastDataValueAsString(DataTypeModelKey.soundPressureLevel);
                    let markerIcon = "blue-marker-noise-sensor.png";
                    switch (device.getAlarmStatus(DataTypeModelKey.soundPressureLevel)) {
                        case EventModelSeverity.EventModelSeverityAlarm:
                            markerIcon = "red-marker-noise-sensor.png";
                            break;
                        case EventModelSeverity.EventModelSeverityWarning:
                            markerIcon = "orange-marker-noise-sensor.png";
                    }
                    if (!device.connected) {
                        markerIcon = "dark-marker-noise-sensor.png";
                    }

                    const date = device.getLastDataDate(DataTypeModelKey.soundPressureLevel);
                    const dateString = date ? formatDate(date, "short", this.locale) : "-";
                    const deviceName = device.name ? device.name : device.baseDeviceData.name;
                    const icon = divIcon({
                        iconSize: new Point(50, 69),
                        iconAnchor: new Point(25, 69),
                        html:
                            '<div class="emma-marker"> \
									<img src="/assets/' +
                            markerIcon +
                            '"> \
									<div class="emma-marker-label"><div class="emma-marker-name">' +
                            deviceName +
                            "</div>" +
                            value +
                            '<div class="emma-marker-date">' +
                            dateString +
                            "</div></div> \
								</div>",
                    });
                    nextLayers.push(
                        marker([device.location.latitude, device.location.longitude], {
                            icon,
                        }).on("click", () => {
                            this.zone.run(() => {
                                this.loadingData = true;
                                this.router.navigate(["/device/" + device.id]);
                            });
                        }),
                    );
                    break;
                }
            }

            //Update map bounds
            if (nextBounds == null) {
                nextBounds = latLngBounds(
                    latLng(device.location.latitude, device.location.longitude),
                    latLng(device.location.latitude, device.location.longitude),
                );
            } else {
                nextBounds.extend(latLng(device.location.latitude, device.location.longitude));
            }
        }

        if (fitBounds) {
            this.bounds = nextBounds;
        }
        this.layers = nextLayers;
        this.cdr.detectChanges();
    }
}
